import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useScannerContext } from '@/contexts/ScannerContext';
import { useNFCReader } from '@/hooks/useNFCReader';
import { api } from '@/lib/apiClient';
import { ChipPayload, fitNtag213, serializePayload, estimateSize } from '@/lib/chipPayload';
import logger from '@/lib/logger';

export interface ChipWriteOptions {
  tagUid: string;
  payload: Partial<ChipPayload>;
  processRecordId?: string;
  autoclaveCycleId?: string;
  /** If true (default), prefer USB bridge first, fallback to mobile. */
  preferUsbFirst?: boolean;
  /** If true, skip the actual NFC write and only register the audit row (for "scheduled write" or testing). */
  serverOnly?: boolean;
}

export interface ChipWriteResult {
  success: boolean;
  device: 'mobile' | 'usb-bridge' | 'none';
  payloadWritten: ChipPayload;
  payloadSizeBytes: number;
  errorMessage?: string;
}

export function useChipWriter() {
  const { user } = useAuth();
  const { nfcBridge, writeTag: writeTagUsb } = useScannerContext();
  const { isSupported: isMobileNfcSupported, writeFullTag } = useNFCReader();
  const [isWriting, setIsWriting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const writeChip = useCallback(
    async (opts: ChipWriteOptions): Promise<ChipWriteResult> => {
      const fullPayload: ChipPayload = fitNtag213({ v: 1, ...opts.payload } as ChipPayload);
      const size = estimateSize(fullPayload);
      let success = false;
      let device: ChipWriteResult['device'] = 'none';
      let errorMessage: string | undefined;

      setIsWriting(true);
      setLastError(null);

      // Check if chip is writable (mobile behavior, not fixed)
      try {
        const { data: canWrite } = await api.rpc('can_write_to_chip', { p_tag_uid: opts.tagUid });
        if (canWrite === false) {
          setIsWriting(false);
          setLastError('No se puede escribir en un chip fijo (molde)');
          return {
            success: false,
            device: 'none',
            payloadWritten: fullPayload,
            payloadSizeBytes: size,
            errorMessage: 'Chip fijo (molde): solo lectura. Los chips de molde no se escriben directamente.',
          };
        }
      } catch (e: any) {
        logger.warn('Could not verify chip behavior, proceeding with write', e);
      }

      if (!opts.serverOnly) {
        const usbReady = nfcBridge.isReaderReady;
        const preferUsb = opts.preferUsbFirst !== false;

        try {
          if (preferUsb && usbReady) {
            const ok = await writeTagUsb({ ...fullPayload });
            if (ok) {
              success = true;
              device = 'usb-bridge';
            } else {
              errorMessage = 'USB write returned false';
            }
          }
          if (!success && isMobileNfcSupported) {
            const ok = await writeFullTag(opts.tagUid, { customData: fullPayload as any });
            if (ok) {
              success = true;
              device = 'mobile';
            } else if (!errorMessage) {
              errorMessage = 'Mobile NFC write failed';
            }
          }
          if (!success && !errorMessage) {
            errorMessage = 'No NFC writer available (no USB bridge, no mobile NFC)';
          }
        } catch (e: any) {
          errorMessage = e?.message || 'Unknown NFC write error';
          logger.error('useChipWriter write failed', e);
        }
      } else {
        success = true;
        device = 'none';
      }

      // Audit trail: register the write attempt regardless of outcome
      try {
        await api.from('nfc_chip_writes').insert({
          tag_uid: opts.tagUid,
          process_record_id: opts.processRecordId || null,
          autoclave_cycle_id: opts.autoclaveCycleId || null,
          payload_written: fullPayload,
          payload_size_bytes: size,
          written_by: user?.id || null,
          device,
          success,
          error_message: errorMessage || null,
        });
      } catch (e: any) {
        logger.error('Failed to insert nfc_chip_writes audit row', e);
      }

      // If success, sync nfc_tags.current_payload as the latest known chip state
      if (success) {
        try {
          await api
            .from('nfc_tags')
            .update({
              current_payload: fullPayload,
              current_payload_at: new Date().toISOString(),
              last_scanned_at: new Date().toISOString(),
              last_scanned_by: user?.id || null,
            })
            .eq('tag_uid', opts.tagUid);
        } catch (e: any) {
          logger.warn('Failed to update nfc_tags.current_payload', e);
        }
      }

      setIsWriting(false);
      if (!success && errorMessage) setLastError(errorMessage);

      return {
        success,
        device,
        payloadWritten: fullPayload,
        payloadSizeBytes: size,
        errorMessage,
      };
    },
    [user, nfcBridge.isReaderReady, isMobileNfcSupported, writeTagUsb, writeFullTag]
  );

  return {
    writeChip,
    isWriting,
    lastError,
    canWrite: nfcBridge.isReaderReady || isMobileNfcSupported,
  };
}
